import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs';
import { AdminApiService } from 'src/app/service/admin/admin-api.service';
import { ThemeService } from 'src/app/service/theme/theme.service';
import { DownloadService } from 'src/app/utils/download/download.service';

// Interface definitions to strictly enforce strong type structures
interface AdminInfo {
  srNo: number;
  adminLoginName: string;
  adminRole: string;
  name: string;
  title?: string;
  department: string;
  phone: string;
  email: string;
  enabled: boolean;
}

interface AdminListResponse {
  adminInfoLst: AdminInfo[];
  total: number;
}

@Component({
  selector: 'app-admin-list',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
  ],
  providers: [DownloadService],
  templateUrl: './admin-list.component.html',
  styleUrls: ['./admin-list.component.css'],
})
export class AdminListComponent implements OnInit {
  // Functional Injection Tokens Engine (Constructorless Pattern)
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly apiService = inject(AdminApiService);
  private readonly downloadService = inject(DownloadService);
  private readonly destroyRef = inject(DestroyRef);

  // Fully Refactored UI State Signals
  readonly admins = signal<AdminInfo[]>([]);
  readonly total = signal<number>(0);
  readonly page = signal<number>(1); // Changed to 1-based index to sync with ngx-pagination
  readonly itemsPerPage = signal<number>(10);

  readonly isCreateAdmin = signal<boolean>(true);
  readonly isError = signal<boolean>(false);
  readonly isUploadError = signal<boolean>(false);
  readonly errorMessage = signal<string>('');
  readonly apiLoading = signal<boolean>(false);
  readonly loading = signal<boolean>(false);
  readonly isDownloading = signal<boolean>(false);
  readonly loginEnableStatus = signal<boolean>(false);

  // Core Data Properties
  readonly roleOptions = [
    'ADMIN',
    'LUCKY_DRAW_ADMIN',
    'REDEEM_PAYMENT_ADMIN',
    'AUDIT_ADMIN',
    'ACCOUNT_ADMIN',
    'LMS_ADMIN'
  ];

  selectedAdminLoginName = '';
  downloadFileName = '';
  fileTarget?: File;
  htmlstring = '';

  // Strongly Typed Form Architecture Containers
  adminLoginForm!: FormGroup;
  adminDataForm!: FormGroup;
  registerAdminForm!: FormGroup;

  ngOnInit(): void {
    this.initializeValidationForms();
    this.fetchAdminRecords();
  }

  /**
   * Initializes modern reactive schema validators for form definitions
   */
  private initializeValidationForms(): void {
    this.adminLoginForm = this.fb.group({
      username: [
        '',
        [
          Validators.required,
          Validators.pattern(/^\S*$/),
          Validators.minLength(5),
        ],
      ],
      password: [this.generateSecureRandomPassword(10), Validators.required],
      role: ['', Validators.required],
    });

    this.adminDataForm = this.fb.group({
      name: ['', Validators.required],
      title: ['', Validators.required],
      department: ['', Validators.required],
      phone: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      email: ['', [Validators.required, Validators.email]],
    });

    this.registerAdminForm = this.fb.group({
      name: ['', Validators.required],
      title: ['', Validators.required],
      department: ['', Validators.required],
      phone: ['959', [Validators.required, Validators.pattern('^[0-9]*$')]],
      email: ['', [Validators.required, Validators.email]],
    });
  }

  /**
   * Dispatches async pipeline streaming request to fetch the admin grid list matrix
   */
  fetchAdminRecords(): void {
    this.loading.set(true);

    // Server-side adaptation parameter matching (ngx-pagination index 1 maps down to server page index 0)
    const zeroBasedPage = this.page() - 1;

    this.apiService.getAdminList(zeroBasedPage)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.loading.set(false)))
      .subscribe({
        next: (value: AdminListResponse) => {
          this.admins.set(value?.adminInfoLst ?? []);
          this.total.set(value?.total ?? 0);
        },
        error: (err: any) => {
          console.error('Failed to retrieve structural admin configurations:', err);
          alert(err?.message || 'Network error occurred while fetching administrative lists.');
        }
      });
  }

  /**
   * Pseudo-random internal credential string sequence creator engine
   */
  private generateSecureRandomPassword(length: number): string {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    }
    return result;
  }

  /**
 * Initializes Step 1 of administrative provisioning routines
 */
  createAdmin(): void {
    if (this.adminLoginForm.invalid) return;

    this.apiLoading.set(true);
    this.isError.set(false);

    this.apiService.getCreateAdmin(this.adminLoginForm.value)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: () => {
          this.isError.set(false);
          this.isCreateAdmin.set(false); // Transitions step view automatically
        },
        error: (err: any) => {
          this.isError.set(true);
          this.errorMessage.set(err?.message || err || 'Failed to initialize administrative credentials.');
        }
      });
  }

  /**
   * Commits the supplementary employee metadata parameters to complete generation
   */
  registerAdmin(): void {
    if (this.registerAdminForm.invalid) return;

    this.apiLoading.set(true);
    this.isError.set(false);

    const loginName = this.adminLoginForm.get('username')?.value || '';
    const metadataPayload = JSON.stringify(this.registerAdminForm.value);

    this.apiService.getRegisterAdmin(loginName, metadataPayload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: () => {
          this.isCreateAdmin.set(true); // Reset creation flow layout wizard step state
          this.isError.set(false);
          this.adminLoginForm.reset();
          this.registerAdminForm.reset();

          this.closeModalViaNativeElement('createAdminModal');
          this.fetchAdminRecords(); // Refresh active dashboard table logs reactive signal matrix

          console.log('Profile registration signature applied safely.');
        },
        error: (err: any) => {
          this.isError.set(true);
          this.errorMessage.set(err?.message || err || 'Failed to complete metadata registration.');
        }
      });
  }

  /**
   * Intercepts standard browser events to stage system files safely
   */
  onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.files && inputElement.files.length > 0) {
      this.fileTarget = inputElement.files[0];
    }
  }

  /**
   * Transmits target CSV spreadsheet binaries over remote stream pipelines
   */
  uploadFile(): void {
    if (!this.fileTarget) {
      this.isUploadError.set(true);
      this.errorMessage.set('Please select a valid local CSV document first.');
      return;
    }

    this.apiLoading.set(true);
    this.isUploadError.set(false);

    this.apiService.getUploadCSVAdmin(this.fileTarget)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: () => {
          // Success routines trigger smoothly
          this.closeModalViaNativeElement('uploadCSVModal');
          this.fetchAdminRecords();
        },
        error: (err: any) => {
          // Explicitly log the error so you can debug server crashes 
          console.error('CSV Ingestion Process Failed:', err);

          // Redundant apiLoading step removed; only manage warning states here
          this.isUploadError.set(true);
          this.errorMessage.set(err?.message || err || 'Parsing anomaly detected during CSV ingestion.');
        }
      });
  }

  /**
   * Downloads matching table backup states locally down to disk arrays
   */
  downloadFile(): void {
    if (!this.downloadFileName) return;

    this.isDownloading.set(true);

    // Safety extension sanitation enforcement pattern
    let formattedFileName = this.downloadFileName;
    if (!formattedFileName.toLowerCase().endsWith('.csv')) {
      formattedFileName += '.csv';
    }

    this.apiService.getDownloadFile(formattedFileName)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isDownloading.set(false))
      )
      .subscribe({
        next: (blobPayload: any) => {
          this.downloadService.downloadFileInLocal(blobPayload, formattedFileName);

          this.downloadFileName = '';
          this.closeModalViaNativeElement('downloadCSVModal');
        },
        error: (err: any) => {
          console.error('File Streaming Protocol Failure:', err);
          alert(err?.message || err || 'Failed to stream target reporting records.');
        }
      });
  }

  /**
   * populates editing target data maps within workspace components safely
   */
  AdminSingleView(loginName: string, adminRef: AdminInfo): void {
    this.htmlstring = '';
    this.selectedAdminLoginName = loginName;
    this.loginEnableStatus.set(adminRef.enabled);

    // Safely patches the visual dashboard input nodes
    this.adminDataForm.patchValue({
      name: adminRef.name,
      title: adminRef.title || '',
      department: adminRef.department,
      phone: adminRef.phone,
      email: adminRef.email,
    });
  }

  /**
   * Commits standalone column updates cleanly back into the server logs
   */
  UpdateAdminData(): void {
    if (this.adminDataForm.invalid) return;

    this.apiLoading.set(true);

    this.apiService.getUpdateAdminData(this.selectedAdminLoginName, this.adminDataForm.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: () => {
          console.log('Administrative node profiles successfully mutated.');

          // Modal closure and table updates only happen on absolute success
          this.closeModalViaNativeElement('adminUpdate');
          this.fetchAdminRecords();
        },
        error: (err: any) => {
          console.error('Critical fault intercept during user schema mutation updates:', err);
          alert(err?.message || err || 'Critical fault intercept during user schema mutation updates.');
        }
      });
  }

  /**
  * Resets administrative login access credentials with random hashes
  */
  ResetAdminPassword(): void {
    const password = this.generateSecureRandomPassword(10);

    if (!confirm('Are you sure you want to reset this administrative account password?')) {
      return;
    }

    this.apiLoading.set(true);
    this.htmlstring = '';

    this.apiService.getResetAdminLoginPasswordService(this.selectedAdminLoginName, password)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: () => {
          // Renders the clean success box directly into the modal update template frame
          this.htmlstring = `
            <div class="alert alert-success d-flex flex-column gap-1 border-0 shadow-sm font-monospace small mb-0 mt-3" role="alert">
              <div class="fw-bold text-success"><i class="fa-solid fa-circle-check me-2"></i>Password Reset Successful</div>
              <hr class="my-1.5 opacity-25 text-success">
              <div><strong>Account:</strong> ${this.selectedAdminLoginName}</div>
              <div><strong>New Pass:</strong> <code class="user-select-all text-dark bg-white px-1.5 py-0.5 rounded border border-success-subtle">${password}</code></div>
            </div>
          `;
        },
        error: (err: any) => {
          console.error('Password reset failure sequence intercept:', err);
          alert(err?.message || err || 'Failed to update administrative node password records.');
        }
      });
  }

  /**
   * Toggles permission flags to activate suspended system log accounts
   */
  AdminLoginEnable(): void {
    if (!confirm('Are you sure you want to restore full login access privileges for this administrator?')) {
      return;
    }

    this.apiLoading.set(true);

    this.apiService.getAdminLoginEnableService(this.selectedAdminLoginName)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          alert(`${res?.Status || 'Success'}: ${res?.Message || 'Profile unlocked.'}`);

          this.loginEnableStatus.set(true); // Mutates local widget view states instantly
          this.closeModalViaNativeElement('adminUpdate');
          this.fetchAdminRecords(); // Refreshes active list logs automatically
        },
        error: (err: any) => {
          console.error('Profile unlocking anomaly encounter:', err);
          alert(err?.message || err || 'Network failure while dispatching activation flags.');
        }
      });
  }

  /**
   * Suspends access permissions immediately across application boundary lines
   */
  AdminLoginDisable(): void {
    if (!confirm('Are you sure you want to revoke system privileges and disable this login account immediately?')) {
      return;
    }

    this.apiLoading.set(true);

    this.apiService.getAdminLoginDisbaleService(this.selectedAdminLoginName)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: (res: any) => {
          alert(`${res?.Status || 'Success'}: ${res?.Message || 'Access privileges revoked.'}`);

          this.loginEnableStatus.set(false);
          this.closeModalViaNativeElement('adminUpdate');
          this.fetchAdminRecords();
        },
        error: (err: any) => {
          console.error('Profile lockdown routine error:', err);
          alert(err?.message || err || 'Network exception encountered during execution.');
        }
      });
  }

  /**
   * Modern Vanilla DOM abstract component utility helper to close modal animations cleanly without jQuery dependencies
   */
  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }

}
